import { useCallback, useEffect, useRef, useState } from 'react';

import type {
  BrowserStitchMessage,
  BrowserStitchState,
} from '@repo/contracts/browser-protocol';

import { trackEvent } from '../analytics/posthog';
import { logger } from '../lib/logger';
import {
  judgeCapturedPage,
  PAGE_VERDICT_MESSAGE,
  type PageVerdict,
} from './page-guard';
import {
  canStitch,
  maxStitchHeight,
  type StitchLayout,
  stitchTiles,
} from './stitch';
import type { CapturedShot, PageStatus } from './use-browser-session';

const log = logger.child({ module: 'instant-run' });

/**
 * The two backstops on a walk. Neither is the normal way it ends — that is
 * reaching the bottom, which most pages do first.
 *
 * Fifty tiles is the Chrome extension's cap, and far more than a page can
 * use: a stitched canvas tops out at 16,383 CSS pixels (see `maxStitchHeight`),
 * which is about 22 desktop tiles. The clock is what protects against the
 * pathological page: an infinite scroll has no bottom to reach, and without a
 * deadline the walk would keep finding more of it for as long as the session
 * lived.
 */
const MAX_TILES = 50;
const WALK_BUDGET_MS = 90_000;

/**
 * How long a page-side stitch step may take. Preparing walks the whole page
 * once for lazy content and then waits for the hero to settle, so it gets
 * longer than a scroll does.
 */
const PREPARE_TIMEOUT_MS = 60_000;
const STEP_TIMEOUT_MS = 20_000;

/**
 * How far each tile advances, as a share of what the scroller shows. The 5%
 * overlap is what keeps a page that snaps or clamps its scroll from leaving a
 * gap; the stitcher draws each tile only up to where the next one begins, so
 * the overlap is never drawn twice.
 */
const TILE_STEP = 0.95;

/**
 * How still the page has to be before the shutter fires, and how long to wait
 * for that.
 *
 * Chromium only emits a screencast frame when something changes on screen, so
 * silence is a real signal that the screen has finished assembling — and it is
 * the signal this needs, because scrolling a section into view is what *starts*
 * its animation. Measured on docpulse.com: its counters read 1,961+ / 65.3%
 * after 1.2s and 3,000+ / 99.9% after 2.6s. A flat wait long enough for that
 * page is wasted on every page that has nothing to animate, and still too
 * short for the next one; waiting for quiet is neither.
 *
 * The ceiling matters as much as the floor. A page with a looping animation, a
 * video or a carousel is never quiet, and without a cap the run would wait for
 * a stillness that is never coming.
 */
const QUIET_MS = 700;
const QUIET_CEILING_MS = 4_000;

/**
 * The same wait, for every tile after the first.
 *
 * Shorter because by then the capture stylesheet has forced every CSS
 * animation and transition to finish in 0.01s, and the preparing pass has
 * already scrolled every section into view once, which is what fires most
 * reveal and counter scripts. What still moves at that point is a video or a
 * carousel that never stops, so a long ceiling only made those pages wait the
 * full four seconds on every tile.
 */
const TILE_QUIET_MS = 400;
const TILE_QUIET_CEILING_MS = 1_500;

/**
 * How long to wait before looking again at a first screen that came back with
 * nothing on it.
 *
 * An app that renders its own body has an empty document for the moment
 * between arriving and hydrating, and quiet does not distinguish that from a
 * page that is simply blank — nothing is moving in either case. Failing the
 * run on the first look is what produced "That page didn't load" on pages that
 * had, in fact, loaded.
 *
 * One retry, not a loop: if a second look a few seconds later still finds
 * nothing, there is nothing to capture and saying so is the honest outcome.
 */
const EMPTY_RETRY_MS = 3_000;

/**
 * How long to wait for a screenshot before giving up on it.
 *
 * A capture that fails answers with an `error`, not a `captured` — and the
 * worker has failures that are nobody's fault: `Page.captureScreenshot` throws
 * "Not attached to an active page" if the page commits a navigation while the
 * screenshot is in flight, which a redirect or a popup can do at any moment.
 *
 * Without a deadline here the walk simply stopped: the promise waiting for
 * that screen was never settled by anything, so the run hung with the browser
 * open and the banner counting a screen that was never coming.
 */
const SHOT_TIMEOUT_MS = 20_000;

/**
 * The shortest a screen can take, before quiet is even considered.
 *
 * Quiet is a necessary signal, not a sufficient one: a page mid-assembly goes
 * briefly still all the time, and `ready` arrives at commit — measured at
 * +800ms on docpulse.com, against a page still laying itself out at +3s. The
 * first screen therefore waits regardless; the rest need only enough of a
 * floor that the scroll they just asked for has begun to land.
 */
const FIRST_FLOOR_MS = 1_500;
const FLOOR_MS = 400;

/** Poll interval while waiting for the page to go quiet. */
const QUIET_TICK_MS = 150;

export type InstantPhase =
  | 'done'
  | 'failed'
  | 'idle'
  | 'preparing'
  | 'reading'
  | 'stitching'
  | 'walking';

/**
 * The walk's one result: the whole page as a single picture.
 *
 * A `CapturedShot` whose image may be a Blob — the stitched PNG is made in
 * the tab and goes straight to storage, with no base64 round trip. When
 * stitching is not possible (an older worker, a browser without a 2D canvas)
 * it is the first screen on its own, exactly as the worker sent it.
 */
export type WalkedPage = Omit<CapturedShot, 'image'> & { image: Blob | string };

/** What stitching needs from the environment. Injected so tests can drive the
 *  stitched path without a canvas; production uses `stitch.ts`. */
export interface Stitcher {
  can: () => boolean;
  stitch: typeof stitchTiles;
}

const DEFAULT_STITCHER: Stitcher = { can: canStitch, stitch: stitchTiles };

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`no answer in ${String(ms)}ms`));
    }, ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err: unknown) => {
        clearTimeout(timer);
        reject(err instanceof Error ? err : new Error(String(err)));
      },
    );
  });
}

/**
 * The instant run: walk the page a screen at a time and stitch the screens
 * into one full-page picture, the way the Floto Chrome extension does.
 *
 * `/browse`. The session opens and navigates exactly as it always does — same
 * boot screen, same screencast, same failure states — and this readies the
 * page (`stitch-prepare`: capture stylesheet, one pass to mount lazy content),
 * then scrolls, waits for the screen to stop moving and shoots, until the page
 * ends. Fixed and sticky elements are neutralised from the second tile on, so
 * a header appears once at the top instead of once per screen. The tiles are
 * joined in the tab and stored as one capture.
 *
 * **Why not Chromium's capture-beyond-viewport.** It renders the whole document
 * *without ever scrolling it*, so every counter, reveal and lazy image below
 * the fold stays in its starting state — on docpulse.com, five headline
 * statistics that all read zero. Scrolling is what a person does, so each tile
 * is what a person would see; stitching them is what makes it one page.
 */
export function useInstantRun({
  capture,
  enabled,
  onDone,
  onPage,
  pageStatus,
  quietFor,
  stitch,
  stitcher = DEFAULT_STITCHER,
}: {
  capture: (opts?: { includeDom?: boolean }) => void;
  enabled: boolean;
  /**
   * Store the page. Resolves false if it could not be stored, which ends the
   * walk without opening a board.
   */
  onPage: (page: WalkedPage) => Promise<boolean>;
  /**
   * The walk is over and the page is stored.
   *
   * Where leaving for the board belongs: navigating away unmounts this
   * session, which abandons the walk mid-page.
   */
  onDone: () => void;
  pageStatus: PageStatus;
  /** How long the page has been visually still. See QUIET_MS. */
  quietFor: () => number;
  /** One page-side stitch step on the worker — see `useBrowserSession`. */
  stitch: (
    type: BrowserStitchMessage['type'],
    y?: number,
  ) => Promise<BrowserStitchState>;
  stitcher?: Stitcher;
}): {
  /** Hand a finished screenshot to the walk. Returns false when the run has no
   *  use for it. */
  receive: (shot: CapturedShot) => boolean;
  pageError: string | null;
  phase: InstantPhase;
  /** Which screen is being taken, 1-based, for the banner. */
  screen: number;
} {
  const [phase, setPhase] = useState<InstantPhase>('idle');
  const [pageError, setPageError] = useState<string | null>(null);
  const [screen, setScreen] = useState(0);
  const startedRef = useRef(false);

  /** Resolves when the worker answers the capture this walk asked for. */
  const pendingRef = useRef<((shot: CapturedShot) => void) | null>(null);

  const receive = useCallback((shot: CapturedShot): boolean => {
    const waiting = pendingRef.current;
    if (!waiting) return false;
    pendingRef.current = null;
    waiting(shot);
    return true;
  }, []);

  // The walk reads these through a ref so the effect below can be a single
  // async function that runs once, rather than a state machine spread across
  // renders. Everything it touches is either a ref or a stable callback.
  const latest = useRef({
    capture,
    onDone,
    onPage,
    quietFor,
    stitch,
    stitcher,
  });
  latest.current = { capture, onDone, onPage, quietFor, stitch, stitcher };

  useEffect(() => {
    if (!enabled || startedRef.current) return;
    if (pageStatus !== 'ready') return;
    startedRef.current = true;

    let abandoned = false;

    /** Wait out the floor, then for the page to stop changing — or give up on
     *  it ever doing so. */
    const untilQuiet = async (
      floorMs: number,
      { ceiling = QUIET_CEILING_MS, quiet = QUIET_MS } = {},
    ) => {
      await sleep(floorMs);
      const deadline = Date.now() + ceiling;
      while (!abandoned && Date.now() < deadline) {
        if (latest.current.quietFor() >= quiet) return;
        await sleep(QUIET_TICK_MS);
      }
    };

    /** Ask for a screenshot and wait for it. Rejects rather than hanging. */
    const shoot = (includeDom: boolean) =>
      new Promise<CapturedShot>((resolve, reject) => {
        const timer = setTimeout(() => {
          pendingRef.current = null;
          reject(new Error('the screenshot was never answered'));
        }, SHOT_TIMEOUT_MS);
        pendingRef.current = (shot) => {
          clearTimeout(timer);
          resolve(shot);
        };
        latest.current.capture({ includeDom });
      });

    /**
     * One screenshot, with a second attempt if the first never came back.
     *
     * The failures this covers are transient by nature — a navigation landing
     * mid-capture detaches the CDP session the worker was shooting through —
     * so asking again a moment later usually just works.
     */
    const shootTwice = async (
      includeDom: boolean,
    ): Promise<CapturedShot | null> => {
      try {
        return await shoot(includeDom);
      } catch (err) {
        if (abandoned) return null;
        log.warn({ err }, 'a screenshot did not arrive — asking again');
        await sleep(1_000);
        if (abandoned) return null;
        try {
          return await shoot(includeDom);
        } catch (retryErr) {
          log.error({ err: retryErr }, 'the browser would not take a screen');
          return null;
        }
      }
    };

    /** `reason` is the verdict's name (`blocked`, `empty`, …) or
     *  `screenshot` — never the page itself. */
    const fail = (message: string, reason: string) => {
      trackEvent('walk_failed', { reason });
      setPhase('failed');
      setPageError(message);
    };

    /**
     * The first screen, judged. A dead domain or a bot wall is whatever loaded
     * *instead* of the site, so it is on screen one — and only screen one is
     * judged, or a real page would be thrown away for a sparse footer.
     */
    const firstScreen = async (): Promise<CapturedShot | null> => {
      setPhase('walking');
      const taken = await shootTwice(true);
      if (abandoned) return null;
      if (!taken) {
        fail(
          "The browser couldn't take a screenshot of that page.",
          'screenshot',
        );
        return null;
      }
      let shot = taken;
      let verdict: PageVerdict = judgeCapturedPage(shot);

      // A page with nothing on it might just not have rendered yet. Look once
      // more before calling it, and judge whatever comes back the second time.
      if (verdict === 'empty') {
        log.info({}, 'first screen came back empty — looking again');
        setPhase('reading');
        await sleep(EMPTY_RETRY_MS);
        if (abandoned) return null;
        setPhase('walking');
        const again = await shootTwice(true);
        if (abandoned) return null;
        if (!again) {
          fail(
            "The browser couldn't take a screenshot of that page.",
            'screenshot',
          );
          return null;
        }
        shot = again;
        verdict = judgeCapturedPage(shot);
      }

      if (verdict !== 'ok') {
        log.warn({ verdict }, 'instant run refused the page');
        fail(PAGE_VERDICT_MESSAGE[verdict], verdict);
        return null;
      }
      return shot;
    };

    const store = async (page: WalkedPage) => {
      const stored = await latest.current.onPage(page);
      if (abandoned || !stored) return;
      setPhase('done');
      latest.current.onDone();
    };

    const walk = async () => {
      setPhase('reading');
      setScreen(1);
      await untilQuiet(FIRST_FLOOR_MS);
      if (abandoned) return;

      // Ready the page. A worker that cannot (an older one answers "Unknown
      // message type") still gets a walk: the first screen, on its own.
      setPhase('preparing');
      let state: BrowserStitchState | null = null;
      try {
        state = await withTimeout(
          latest.current.stitch('stitch-prepare'),
          PREPARE_TIMEOUT_MS,
        );
      } catch (err) {
        log.warn({ err }, 'stitching unavailable — keeping one screen');
      }
      if (abandoned) return;

      setPhase('reading');
      await untilQuiet(FLOOR_MS);
      if (abandoned) return;

      const first = await firstScreen();
      if (abandoned || !first) return;
      if (!state) {
        await store(first);
        return;
      }

      const background = state.background;
      const clamp = maxStitchHeight(first.width * first.dpr, first.dpr);

      /** Join the tiles into one picture and store it. */
      const save = async (
        positions: number[],
        tiles: string[],
        layout: StitchLayout,
      ) => {
        if (!latest.current.stitcher.can()) {
          await store(first);
          return;
        }
        try {
          const image = await latest.current.stitcher.stitch({
            background,
            dpr: first.dpr,
            layout,
            positions,
            tiles,
          });
          if (abandoned) return;
          await store({
            ...first,
            fullPage: true,
            height: layout.finalHeight,
            image,
          });
        } catch (err) {
          log.error({ err }, 'stitching failed — keeping the first screen');
          trackEvent('walk_failed', { reason: 'stitch', recovered: true });
          if (abandoned) return;
          await store(first);
        }
      };

      // A window that will not scroll. Either the page turns the wheel into
      // something else — a scroll-jacked "scene" of slides, lemfi.com — or a
      // dialog locked it. Turn the wheel a screen at a time and shoot what
      // each turn shows, stacked; a wheel that moves nothing means a lock,
      // which is lifted for the ordinary walk below.
      if (state.locked) {
        const wheelTiles = [first.image];
        const wheelDeadline = Date.now() + WALK_BUDGET_MS;
        while (
          wheelTiles.length < MAX_TILES &&
          (wheelTiles.length + 1) * state.viewportHeight <= clamp &&
          Date.now() < wheelDeadline
        ) {
          setScreen(wheelTiles.length + 1);
          setPhase('reading');
          let turned: BrowserStitchState;
          try {
            turned = await withTimeout(
              latest.current.stitch('stitch-wheel'),
              STEP_TIMEOUT_MS,
            );
          } catch (err) {
            log.warn({ err }, 'a wheel turn failed — stitching what we have');
            break;
          }
          if (abandoned) return;
          if (!turned.moved) break;
          await untilQuiet(FLOOR_MS, {
            ceiling: TILE_QUIET_CEILING_MS,
            quiet: TILE_QUIET_MS,
          });
          if (abandoned) return;
          setPhase('walking');
          const shot = await shootTwice(false);
          if (abandoned) return;
          if (!shot) break;
          wheelTiles.push(shot.image);
        }

        if (wheelTiles.length > 1) {
          log.info(
            { screens: wheelTiles.length },
            'walked a wheel-driven page',
          );
          setPhase('stitching');
          try {
            await withTimeout(
              latest.current.stitch('stitch-finish'),
              STEP_TIMEOUT_MS,
            );
          } catch (err) {
            log.warn({ err }, 'could not restore the page after stitching');
          }
          if (abandoned) return;
          const height = state.viewportHeight;
          const total = wheelTiles.length * height;
          await save(
            wheelTiles.map((_, i) => i * height),
            wheelTiles,
            {
              containerBottom: height,
              containerTop: 0,
              contentHeight: total,
              finalHeight: total,
              viewportHeight: height,
            },
          );
          return;
        }

        try {
          state = await withTimeout(
            latest.current.stitch('stitch-unlock'),
            STEP_TIMEOUT_MS,
          );
        } catch (err) {
          log.warn({ err }, 'could not lift the scroll lock');
        }
        if (abandoned) return;
      }

      const containerHeight = state.containerBottom - state.containerTop;
      const step = Math.max(1, Math.floor(containerHeight * TILE_STEP));
      const positions = [state.actualY];
      const tiles = [first.image];
      const deadline = Date.now() + WALK_BUDGET_MS;

      while (tiles.length < MAX_TILES) {
        const last = positions[positions.length - 1] ?? 0;
        if (last >= state.maxScroll - 5) break;
        // Everything a canvas can hold is already covered.
        if (state.containerTop + last + containerHeight >= clamp) break;
        if (Date.now() > deadline) {
          log.info({ tiles: tiles.length }, 'walk hit its time budget');
          break;
        }

        setScreen(tiles.length + 1);
        setPhase('reading');
        let next: BrowserStitchState;
        try {
          next = await withTimeout(
            latest.current.stitch('stitch-scroll', last + step),
            STEP_TIMEOUT_MS,
          );
        } catch (err) {
          log.warn({ err }, 'a stitch scroll failed — stitching what we have');
          break;
        }
        if (abandoned) return;
        await untilQuiet(FLOOR_MS, {
          ceiling: TILE_QUIET_CEILING_MS,
          quiet: TILE_QUIET_MS,
        });
        if (abandoned) return;

        setPhase('walking');
        const shot = await shootTwice(false);
        if (abandoned) return;
        // Nothing more is coming from this browser. What is already shot is a
        // real, if short, page — better than failing a walk that had tiles.
        if (!shot) break;
        // A scroll that moved nothing has reached the bottom; this tile is
        // the previous one again.
        if (next.actualY <= last) break;
        positions.push(next.actualY);
        tiles.push(shot.image);
        state = next;
        if (next.actualY >= next.maxScroll - 2) break;
      }

      setPhase('stitching');
      try {
        state = await withTimeout(
          latest.current.stitch('stitch-finish'),
          STEP_TIMEOUT_MS,
        );
      } catch (err) {
        // The page stays styled for capture, which nobody sees again — the
        // session is abandoned once the board opens. The last measurement
        // still describes what the tiles cover.
        log.warn({ err }, 'could not restore the page after stitching');
      }
      if (abandoned) return;

      // Never taller than the tiles reach: a walk that stopped early (a page
      // that would not scroll on, the time budget) ends where its last tile
      // does, rather than padding the picture with blank page.
      const reached =
        state.containerTop +
        (positions[positions.length - 1] ?? 0) +
        containerHeight +
        (state.viewportHeight - state.containerBottom);
      await save(positions, tiles, {
        containerBottom: state.containerBottom,
        containerTop: state.containerTop,
        contentHeight: state.contentHeight,
        finalHeight: Math.min(state.totalHeight, clamp, reached),
        viewportHeight: state.viewportHeight,
      });
    };

    void walk();

    return () => {
      abandoned = true;
      pendingRef.current = null;
    };
  }, [enabled, pageStatus]);

  return { pageError, phase, receive, screen };
}
